import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardActionArea,
  LinearProgress,
  Badge,
  Avatar,
  Divider,
  Chip,
} from "@mui/material"
import {
  CloudOff,
  CloudDone,
  CameraAlt,
  History,
  ReportProblem,
  Sync,
  WhereToVote,
  LocationOn,
  Person,
  Public,
} from "@mui/icons-material"
import localforage from "localforage"
import dayjs from "dayjs"

import Header from "../../../../layouts/Header"
import { api } from "../../../../api"

// Tema adaptado
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const DashboardDeleMesa = () => {
  const navigate = useNavigate()
  const [isOffline, setIsOffline] = useState(!navigator.onLine)

  // Estados de métricas logísticas y RLS
  const [juntasAsignadas, setJuntasAsignadas] = useState(0)
  const [itemsPendientesSync, setItemsPendientesSync] = useState(0)
  const [recintoActual, setRecintoActual] = useState("Cargando cobertura...")
  const [esGlobal, setEsGlobal] = useState(false)

  // ========================================================
  // 1. MONITOREO DE RED Y CARGA DE METADATA
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarEstadisticas()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const cargarEstadisticas = async () => {
    // 1. Revisar si hay trabajo atrasado sin subir (IndexedDB)
    const actas = (await localforage.getItem("actas_offline_queue")) || []
    const incidencias = (await localforage.getItem("incidencias_offline_queue")) || []
    setItemsPendientesSync(actas.length + incidencias.length)

    // 2. Extraer el contexto real del backend (RLS)
    try {
      if (!navigator.onLine) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getDatosDelegado", {})
      const data = res?.data !== undefined ? res.data : res

      if (data && data.success) {
        const isGlobalBackend = data.es_global || false
        const totalJuntas = data.data?.juntas?.length || 0
        const recintoAsignado = data.recinto || "Desconocido"

        setEsGlobal(isGlobalBackend)
        setRecintoActual(recintoAsignado)
        setJuntasAsignadas(totalJuntas)

        // Guardamos en caché para visualización offline
        await localforage.setItem("dash_dele_global", isGlobalBackend)
        await localforage.setItem("dash_dele_recinto", recintoAsignado)
        await localforage.setItem("dash_dele_juntas_qty", totalJuntas)
      }
    } catch (error) {
      console.log("Cargando dashboard del delegado desde caché offline...")

      const gCache = (await localforage.getItem("dash_dele_global")) || false
      const rCache = (await localforage.getItem("dash_dele_recinto")) || "Sin recinto asignado"
      const jQtyCache = (await localforage.getItem("dash_dele_juntas_qty")) || 0

      setEsGlobal(gCache)
      setRecintoActual(rCache)
      setJuntasAsignadas(jQtyCache)
    }
  }

  // Rutas de navegación a los módulos ya creados
  const nav = {
    ingreso: "/home/dashboard/ControlElectoral/Delegado/IngresoActa",
    misActas: "/home/dashboard/ControlElectoral/Delegado/MisActas",
    incidencia: "/home/dashboard/ControlElectoral/Delegado/ReportarIncidencia",
    sync: "/home/dashboard/ControlElectoral/Delegado/Sincronizacion",
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />

      {/* Contenedor principal móvil */}
      <div
        className="main main-app p-2"
        style={{ backgroundColor: "#f0f2f5", minHeight: "100vh", paddingBottom: "80px" }}
      >
        {/* BARRA SUPERIOR DE RED */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? (
              <CloudOff color="error" sx={{ fontSize: 28, mr: 1 }} />
            ) : (
              <CloudDone color="success" sx={{ fontSize: 28, mr: 1 }} />
            )}
            <Typography variant="subtitle2" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "MODO OFFLINE (SIN SEÑAL)" : "CONECTADO A LA RED"}
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" fontWeight="bold">
            {dayjs().format("DD/MM/YYYY")}
          </Typography>
        </Box>

        {/* TARJETA DE PERFIL (DINÁMICA RLS) */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: esGlobal
              ? "linear-gradient(135deg, #0d3b4a 0%, #051c24 100%)"
              : "linear-gradient(135deg, #196C87 0%, #114b5e 100%)",
            color: "white",
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 56, height: 56, mr: 2 }}>
              {esGlobal ? <Public fontSize="large" /> : <Person fontSize="large" />}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {esGlobal ? "Administrador Global" : "Operativo Electoral"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {esGlobal ? "Centro de Digitación" : "Delegado de Mesa"}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)", mb: 2 }} />

          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box display="flex" alignItems="flex-start">
              <LocationOn sx={{ mr: 1, mt: 0.5, opacity: 0.8, color: esGlobal ? "#4fc3f7" : "inherit" }} />
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: "uppercase" }}>
                  Cobertura Asignada
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2} mt={0.5}>
                  {recintoActual}
                </Typography>
              </Box>
            </Box>
            {esGlobal && <Chip label="MODO GLOBAL" color="info" size="small" sx={{ fontWeight: "bold", mt: 1 }} />}
          </Box>
        </Paper>

        {/* INDICADOR DE AVANCE */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: "white" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              <WhereToVote sx={{ verticalAlign: "middle", mr: 0.5 }} /> Alcance Asignado
            </Typography>
            <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
              Mesas: {juntasAsignadas}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={0}
            sx={{ height: 8, borderRadius: 4, mb: 1, bgcolor: "#e0e0e0" }}
          />
          <Typography variant="caption" color="textSecondary" display="block" textAlign="right">
            El progreso real se visualiza en la central.
          </Typography>
        </Paper>

        {/* MENÚ DE BOTONES GIGANTES (THUMB-FRIENDLY) */}
        <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom sx={{ ml: 1 }}>
          Opciones de Operación
        </Typography>

        <Grid container spacing={2} pb={4}>
          {/* 1. Ingresar Acta (Botón Principal) */}
          <Grid item xs={12}>
            <Card elevation={3} sx={{ borderRadius: 3, bgcolor: "#196C87", color: "white" }}>
              <CardActionArea onClick={() => navigate(nav.ingreso)} sx={{ p: 2 }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 60, height: 60, mr: 2 }}>
                    <CameraAlt fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      Ingresar Acta
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Capturar foto y digitar votos
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>

          {/* 2. Historial de Actas */}
          <Grid item xs={6}>
            <Card elevation={2} sx={{ borderRadius: 3, height: "100%" }}>
              <CardActionArea
                onClick={() => navigate(nav.misActas)}
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <History sx={{ fontSize: 48, color: "#196C87", mb: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" color="textPrimary">
                  Mis Actas
                </Typography>
                <Typography variant="caption" color="textSecondary" textAlign="center">
                  Ver historial enviado
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>

          {/* 3. Sincronización (Con Badge de Alerta) */}
          <Grid item xs={6}>
            <Badge badgeContent={itemsPendientesSync} color="secondary" sx={{ width: "100%", height: "100%" }}>
              <Card
                elevation={2}
                sx={{ borderRadius: 3, width: "100%", border: itemsPendientesSync > 0 ? "2px solid #ed6c02" : "none" }}
              >
                <CardActionArea
                  onClick={() => navigate(nav.sync)}
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Sync sx={{ fontSize: 48, color: itemsPendientesSync > 0 ? "#ed6c02" : "#2E7D32", mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="textPrimary">
                    Sincronizar
                  </Typography>
                  <Typography variant="caption" color="textSecondary" textAlign="center">
                    {itemsPendientesSync > 0 ? "Datos pendientes" : "Todo al día"}
                  </Typography>
                </CardActionArea>
              </Card>
            </Badge>
          </Grid>

          {/* 4. Reportar Incidencia (Botón de Pánico) */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 3, borderLeft: "6px solid #d32f2f" }}>
              <CardActionArea onClick={() => navigate(nav.incidencia)} sx={{ p: 2 }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: "#ffebee", color: "#d32f2f", mr: 2 }}>
                    <ReportProblem />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="error.main">
                      Reportar Incidencia
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Problemas de mesa, seguridad o faltantes
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </div>
    </ThemeProvider>
  )
}

export default DashboardDeleMesa
